import {IconShieldCheck} from "@tabler/icons-react";
import {Alert, DatePicker, Form, Input, Select, Typography} from "@ui";
import moment from "moment";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton} from "@/components/Button";
import {
  formatBirthdate,
  isAdultFromBirthdate,
  normalizeChileanRUT,
  validateChileanRUT,
} from "@/util/identityValidation";
import styles from "./identity-document-form.module.css";

const ID_TYPES = ["V", "E", "RUT"];

const IdentityDocumentForm = ({
  onSubmit,
  isSubmitting = false,
  error,
  submitLabel,
  title,
  description,
  forceManual = false,
  manualFallbackHint,
  onIdTypeChange,
}) => {
  const {t} = useTranslation();
  const [idType, setIdType] = useState("V");
  const [nationalId, setNationalId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState(null);
  const [fieldError, setFieldError] = useState("");
  const [manualFieldErrors, setManualFieldErrors] = useState({});

  const showManualFields = idType === "RUT" || forceManual;

  const handleIdTypeChange = value => {
    setIdType(value);
    setFieldError("");
    setManualFieldErrors({});
    onIdTypeChange?.(value);
    if (value !== "RUT") {
      setNationalId(prev => prev.replace(/\D/g, "").slice(0, 10));
    }
  };

  const validateDocument = () => {
    if (idType === "RUT") {
      if (!nationalId.trim()) {
        setFieldError(t("identityRutRequired"));
        return false;
      }
      if (!validateChileanRUT(nationalId)) {
        setFieldError(t("identityRutInvalid"));
        return false;
      }
      return true;
    }

    const digits = nationalId.replace(/\D/g, "");
    if (!digits) {
      setFieldError(t("identityCedulaRequired"));
      return false;
    }
    if (!/^\d{6,10}$/.test(digits)) {
      setFieldError(t("identityCedulaLength"));
      return false;
    }
    return true;
  };

  const validateManualFields = () => {
    const errors = {};

    if (!firstName.trim()) {
      errors.firstName = t("identityFirstNameRequired");
    }
    if (!lastName.trim()) {
      errors.lastName = t("identityLastNameRequired");
    }
    if (!birthDate) {
      errors.birthDate = t("identityBirthdateRequired");
    } else if (!isAdultFromBirthdate(birthDate)) {
      errors.birthDate = t("identityError_underage");
    }

    setManualFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    setFieldError("");
    setManualFieldErrors({});

    if (!validateDocument()) {
      return;
    }

    const documentValue = idType === "RUT" ? normalizeChileanRUT(nationalId) : nationalId.replace(/\D/g, "");

    if (showManualFields) {
      if (!validateManualFields()) {
        return;
      }

      onSubmit({
        idType,
        nationalId: documentValue,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        birthDate: formatBirthdate(birthDate),
      });
      return;
    }

    onSubmit({idType, nationalId: documentValue});
  };

  const submitText = isSubmitting
    ? showManualFields
      ? t("identityFormSaving")
      : t("identityFormVerifying")
    : (submitLabel ?? (showManualFields ? t("identityFormSave") : t("identityFormSubmit")));

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.iconWrap}>
          <IconShieldCheck
            size={24}
            stroke={1.75}
          />
        </div>
        <div>
          <Typography.Title
            level={5}
            className={styles.title}
          >
            {title ?? t("identityFormTitle")}
          </Typography.Title>
          <Typography.Text className={styles.description}>
            {description ?? t("identityFormDescription")}
          </Typography.Text>
        </div>
      </div>

      <Form
        layout="vertical"
        onFinish={handleSubmit}
        className={styles.form}
      >
        <Form.Item
          label={t("identityCedulaLabel")}
          validateStatus={fieldError ? "error" : undefined}
          help={fieldError || undefined}
          className={styles.field}
        >
          <div className={styles.cedulaRow}>
            <Select
              value={idType}
              onChange={handleIdTypeChange}
              disabled={isSubmitting}
              options={ID_TYPES.map(type => ({value: type, label: type}))}
              className={styles.idTypeSelect}
              aria-label={t("identityIdTypeLabel")}
            />
            <div className={styles.idInputWrap}>
              <Input
                type="text"
                inputMode={idType === "RUT" ? "text" : "numeric"}
                maxLength={idType === "RUT" ? 12 : 10}
                value={nationalId}
                onChange={e => {
                  const value =
                    idType === "RUT"
                      ? e.target.value.replace(/[^0-9kK-]/g, "").slice(0, 12)
                      : e.target.value.replace(/\D/g, "").slice(0, 10);
                  setNationalId(value);
                  setFieldError("");
                }}
                placeholder={idType === "RUT" ? t("identityRutPlaceholder") : "12345678"}
                disabled={isSubmitting}
                className={styles.idInput}
              />
            </div>
          </div>
        </Form.Item>

        {showManualFields && (
          <>
            {manualFallbackHint && (
              <Alert
                type="info"
                showIcon
                message={manualFallbackHint}
                className={styles.alert}
              />
            )}

            <Form.Item
              label={t("identityFirstNameLabel")}
              validateStatus={manualFieldErrors.firstName ? "error" : undefined}
              help={manualFieldErrors.firstName || undefined}
              className={styles.field}
            >
              <Input
                value={firstName}
                onChange={e => {
                  setFirstName(e.target.value);
                  setManualFieldErrors(prev => ({...prev, firstName: undefined}));
                }}
                disabled={isSubmitting}
                placeholder={t("identityFirstNamePlaceholder")}
              />
            </Form.Item>

            <Form.Item
              label={t("identityLastNameLabel")}
              validateStatus={manualFieldErrors.lastName ? "error" : undefined}
              help={manualFieldErrors.lastName || undefined}
              className={styles.field}
            >
              <Input
                value={lastName}
                onChange={e => {
                  setLastName(e.target.value);
                  setManualFieldErrors(prev => ({...prev, lastName: undefined}));
                }}
                disabled={isSubmitting}
                placeholder={t("identityLastNamePlaceholder")}
              />
            </Form.Item>

            <Form.Item
              label={t("identityBirthdateLabel")}
              validateStatus={manualFieldErrors.birthDate ? "error" : undefined}
              help={manualFieldErrors.birthDate || undefined}
              className={styles.field}
            >
              <DatePicker
                value={birthDate}
                onChange={value => {
                  setBirthDate(value);
                  setManualFieldErrors(prev => ({...prev, birthDate: undefined}));
                }}
                disabled={isSubmitting}
                format="DD/MM/YYYY"
                placeholder={t("identityBirthdatePlaceholder")}
                disabledDate={current => current && current > moment().endOf("day")}
                className={styles.birthDate}
                getPopupContainer={trigger => trigger.parentNode}
                placement="topLeft"
              />
            </Form.Item>
          </>
        )}

        {error && !fieldError && !manualFieldErrors.birthDate && (
          <Alert
            type="error"
            showIcon
            message={t("identityFormErrorTitle")}
            description={error}
            className={styles.alert}
          />
        )}

        <PrimaryButton
          htmlType="submit"
          loading={isSubmitting}
          disabled={isSubmitting}
          className={styles.submit}
        >
          {submitText}
        </PrimaryButton>
      </Form>
    </div>
  );
};

export default IdentityDocumentForm;
